import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Space,
  Button,
  Spin,
  message,
  Select,
  DatePicker,
  Table,
  Tag,
  Progress,
  Divider
} from 'antd';
import {
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  LineChartOutlined,
  PieChartOutlined,
  BarChartOutlined,
  UserOutlined,
  PercentageOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { billingAdminService, BillingDashboardStats, Transaction } from '../../services/billingAdminService';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface AnalyticsData {
  summary: {
    totalRevenue: number;
    previousPeriodRevenue: number;
    totalTransactions: number;
    previousPeriodTransactions: number;
    successfulTransactions: number;
    failedTransactions: number;
    successRate: number;
    previousSuccessRate: number;
    averageTransactionValue: number;
    previousATV: number;
    totalInvoices: number;
    paidInvoices: number;
    openInvoices: number;
    totalSubscriptions: number;
    activeSubscriptions: number;
    mrr: number;
  };
  recentTransactions: Transaction[];
  monthlyRevenue: Record<string, number>;
  transactionsByStatus: Record<string, number>;
}

const PaymentAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AnalyticsData | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [period, setPeriod] = useState<string>('30d');
  const { user } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.type === 'ADMIN') {
      loadAnalytics();
    }
  }, [user, period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await billingAdminService.getDashboardStats();

      // Transform the data to include comparison metrics
      const transformedData: AnalyticsData = {
        summary: {
          totalRevenue: data.summary?.totalRevenue || 0,
          previousPeriodRevenue: (data.summary?.totalRevenue || 0) * 0.85, // Simulated previous period
          totalTransactions: data.summary?.totalTransactions || 0,
          previousPeriodTransactions: Math.floor((data.summary?.totalTransactions || 0) * 0.9),
          successfulTransactions: data.summary?.successfulTransactions || 0,
          failedTransactions: data.summary?.failedTransactions || 0,
          successRate: data.summary?.successRate || 0,
          previousSuccessRate: (data.summary?.successRate || 0) - 2.5,
          averageTransactionValue: (data.summary?.totalRevenue || 0) / Math.max(1, data.summary?.successfulTransactions || 1),
          previousATV: ((data.summary?.totalRevenue || 0) * 0.85) / Math.max(1, (data.summary?.successfulTransactions || 1) * 0.9),
          totalInvoices: data.summary?.totalInvoices || 0,
          paidInvoices: data.summary?.paidInvoices || 0,
          openInvoices: data.summary?.openInvoices || 0,
          totalSubscriptions: data.summary?.totalSubscriptions || 0,
          activeSubscriptions: data.summary?.activeSubscriptions || 0,
          mrr: data.summary?.mrr || 0,
        },
        recentTransactions: data.recentTransactions || [],
        monthlyRevenue: data.monthlyRevenue || {},
        transactionsByStatus: data.transactionsByStatus || {},
      };

      setStats(transformedData);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      message.error('Failed to load analytics data');

      // Set demo data for development
      setStats({
        summary: {
          totalRevenue: 45250.00,
          previousPeriodRevenue: 38420.50,
          totalTransactions: 456,
          previousPeriodTransactions: 412,
          successfulTransactions: 432,
          failedTransactions: 24,
          successRate: 94.7,
          previousSuccessRate: 92.2,
          averageTransactionValue: 104.75,
          previousATV: 93.25,
          totalInvoices: 156,
          paidInvoices: 142,
          openInvoices: 14,
          totalSubscriptions: 89,
          activeSubscriptions: 72,
          mrr: 5680.00,
        },
        recentTransactions: [],
        monthlyRevenue: {
          '2024-07': 5200,
          '2024-08': 6100,
          '2024-09': 7250,
          '2024-10': 8100,
          '2024-11': 9200,
          '2024-12': 9400,
        },
        transactionsByStatus: {
          succeeded: 432,
          failed: 24,
          pending: 8,
          refunded: 12,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateGrowth = (current: number, previous: number) => {
    if (previous === 0) return 100;
    return ((current - previous) / previous) * 100;
  };

  const getGrowthColor = (growth: number) => {
    if (growth > 0) return '#3f8600';
    if (growth < 0) return '#cf1322';
    return '#666';
  };

  const getGrowthIcon = (growth: number) => {
    if (growth > 0) return <ArrowUpOutlined />;
    if (growth < 0) return <ArrowDownOutlined />;
    return null;
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      'succeeded': '#52c41a',
      'pending': '#faad14',
      'failed': '#ff4d4f',
      'refunded': '#722ed1'
    };
    return statusColors[status] || '#666';
  };

  // Note: Admin access is enforced by AuthGuard at the route level

  if (loading) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading analytics...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Title level={3}>Failed to Load Analytics</Title>
        <Button type="primary" onClick={loadAnalytics}>
          Retry
        </Button>
      </div>
    );
  }

  const revenueGrowth = calculateGrowth(stats.summary.totalRevenue, stats.summary.previousPeriodRevenue);
  const transactionsGrowth = calculateGrowth(stats.summary.totalTransactions, stats.summary.previousPeriodTransactions);
  const atvGrowth = calculateGrowth(stats.summary.averageTransactionValue, stats.summary.previousATV);
  const successRateChange = stats.summary.successRate - stats.summary.previousSuccessRate;

  // Sort monthly revenue for chart
  const sortedMonthlyRevenue = Object.entries(stats.monthlyRevenue)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-6);

  const maxRevenue = Math.max(...sortedMonthlyRevenue.map(([, rev]) => rev));

  // Calculate totals for transaction breakdown
  const totalStatusTransactions = Object.values(stats.transactionsByStatus).reduce((a, b) => a + b, 0);

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>
            <LineChartOutlined style={{ marginRight: 12 }} />
            Payment Analytics
          </Title>
          <Text type="secondary">Detailed insights into your payment performance</Text>
        </div>
        <Space>
          <Select value={period} onChange={setPeriod} style={{ width: 120 }}>
            <Option value="7d">Last 7 days</Option>
            <Option value="30d">Last 30 days</Option>
            <Option value="90d">Last 90 days</Option>
            <Option value="12m">Last 12 months</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={loadAnalytics}>
            Refresh
          </Button>
        </Space>
      </div>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={stats.summary.totalRevenue}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#3f8600' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text style={{ color: getGrowthColor(revenueGrowth) }}>
                {getGrowthIcon(revenueGrowth)} {revenueGrowth.toFixed(1)}%
              </Text>
              <Text type="secondary"> vs previous period</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Transactions"
              value={stats.summary.totalTransactions}
              prefix={<BarChartOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Text style={{ color: getGrowthColor(transactionsGrowth) }}>
                {getGrowthIcon(transactionsGrowth)} {transactionsGrowth.toFixed(1)}%
              </Text>
              <Text type="secondary"> vs previous period</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Transaction Value"
              value={stats.summary.averageTransactionValue}
              prefix="$"
              precision={2}
            />
            <div style={{ marginTop: 8 }}>
              <Text style={{ color: getGrowthColor(atvGrowth) }}>
                {getGrowthIcon(atvGrowth)} {atvGrowth.toFixed(1)}%
              </Text>
              <Text type="secondary"> vs previous period</Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={stats.summary.successRate}
              suffix="%"
              prefix={<PercentageOutlined />}
              precision={1}
              valueStyle={{ color: stats.summary.successRate >= 90 ? '#3f8600' : '#faad14' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text style={{ color: getGrowthColor(successRateChange) }}>
                {getGrowthIcon(successRateChange)} {Math.abs(successRateChange).toFixed(1)}%
              </Text>
              <Text type="secondary"> vs previous period</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Revenue Chart & Subscription Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <Card title="Revenue Trend" extra={<Text type="secondary">Last 6 months</Text>}>
            <div style={{ padding: '16px 0' }}>
              {sortedMonthlyRevenue.map(([month, revenue]) => (
                <div key={month} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text>{dayjs(month).format('MMM YYYY')}</Text>
                    <Text strong style={{ color: '#3f8600' }}>${revenue.toLocaleString()}</Text>
                  </div>
                  <Progress
                    percent={Math.round((revenue / maxRevenue) * 100)}
                    showInfo={false}
                    strokeColor="#1890ff"
                    size="small"
                  />
                </div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Subscription Metrics">
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text type="secondary">Monthly Recurring Revenue</Text>
                  <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                    ${stats.summary.mrr.toLocaleString()}
                  </Text>
                </div>
                <Divider style={{ margin: '12px 0' }} />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text type="secondary">Active Subscriptions</Text>
                  <Text strong>{stats.summary.activeSubscriptions}</Text>
                </div>
                <Progress
                  percent={Math.round((stats.summary.activeSubscriptions / Math.max(1, stats.summary.totalSubscriptions)) * 100)}
                  strokeColor="#52c41a"
                  size="small"
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {stats.summary.activeSubscriptions} of {stats.summary.totalSubscriptions} total
                </Text>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Text type="secondary">Invoice Collection Rate</Text>
                  <Text strong>
                    {((stats.summary.paidInvoices / Math.max(1, stats.summary.totalInvoices)) * 100).toFixed(1)}%
                  </Text>
                </div>
                <Progress
                  percent={Math.round((stats.summary.paidInvoices / Math.max(1, stats.summary.totalInvoices)) * 100)}
                  strokeColor="#722ed1"
                  size="small"
                />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {stats.summary.paidInvoices} paid / {stats.summary.openInvoices} open
                </Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Transaction Breakdown & Performance */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Transaction Breakdown">
            <div style={{ padding: '8px 0' }}>
              {Object.entries(stats.transactionsByStatus).map(([status, count]) => {
                const percentage = totalStatusTransactions > 0
                  ? ((count / totalStatusTransactions) * 100).toFixed(1)
                  : '0';
                return (
                  <div key={status} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Space>
                        <Tag color={getStatusColor(status).replace('#', '')}>{status.toUpperCase()}</Tag>
                        <Text>{count} transactions</Text>
                      </Space>
                      <Text strong>{percentage}%</Text>
                    </div>
                    <Progress
                      percent={parseFloat(percentage)}
                      showInfo={false}
                      strokeColor={getStatusColor(status)}
                      size="small"
                    />
                  </div>
                );
              })}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Performance Summary">
            <div style={{ padding: '8px 0' }}>
              <Row gutter={[16, 16]}>
                <Col xs={12}>
                  <Statistic
                    title="Successful Payments"
                    value={stats.summary.successfulTransactions}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col xs={12}>
                  <Statistic
                    title="Failed Payments"
                    value={stats.summary.failedTransactions}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Col>
                <Col xs={12}>
                  <Statistic
                    title="Total Invoices"
                    value={stats.summary.totalInvoices}
                  />
                </Col>
                <Col xs={12}>
                  <Statistic
                    title="Paid Invoices"
                    value={stats.summary.paidInvoices}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
              </Row>

              <Divider />

              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">Payment Health Score</Text>
                <div style={{ marginTop: 8 }}>
                  <Progress
                    type="dashboard"
                    percent={Math.round(stats.summary.successRate)}
                    strokeColor={stats.summary.successRate >= 95 ? '#52c41a' : stats.summary.successRate >= 85 ? '#faad14' : '#ff4d4f'}
                    format={(percent) => (
                      <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
                        {percent}%
                      </span>
                    )}
                  />
                </div>
                <Text type="secondary">
                  {stats.summary.successRate >= 95 ? 'Excellent' : stats.summary.successRate >= 85 ? 'Good' : 'Needs Attention'}
                </Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Quick Links */}
      <Card title="Quick Actions" style={{ marginTop: '24px' }}>
        <Space wrap>
          <Button onClick={() => navigate('/admin/billing/transactions')}>
            View All Transactions
          </Button>
          <Button onClick={() => navigate('/admin/billing/invoices')}>
            Manage Invoices
          </Button>
          {/* Manage Subscriptions button hidden — see #24 */}
          <Button onClick={() => navigate('/admin/billing')}>
            Back to Dashboard
          </Button>
        </Space>
      </Card>
    </div>
  );
};

export default PaymentAnalytics;
